import React, { useEffect, useRef } from 'react';
import { StatusBar } from 'expo-status-bar';
import { Platform, View, Text, StyleSheet, Dimensions } from 'react-native';
import * as SplashScreen from 'expo-splash-screen';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const isMobileWeb = Platform.OS === 'web' && SCREEN_WIDTH < 768;
import AppNavigator from './src/navigation/AppNavigator';
import { AuthProvider, ClerkProvider, ClerkLoaded, ClerkLoading, CLERK_PUBLISHABLE_KEY } from './src/services/AuthContext';
import { ToastProvider } from './src/contexts/ToastContext';
import { useAuth } from './src/services/AuthContext';
import { useToast } from './src/contexts/ToastContext';
import { ChatCacheProvider } from './src/contexts/ChatCacheContext';
import { ChatNotificationProvider } from './src/contexts/ChatNotificationContext';
import { ThemeProvider, useTheme } from './src/contexts/ThemeContext';
import { MapProviderProvider } from './src/contexts/MapProviderContext';
import { MapSettingsProvider } from './src/contexts/MapSettingsContext';
import { IconThemeProvider } from './src/contexts/IconThemeContext';
import NotificationService from './src/services/NotificationService';
import { supabase } from './src/services/supabase';
import { getPendingFriendRequests, getUserById } from './src/services/database';
import { getProfilePictureUrl } from './src/services/storage';
import ErrorBoundary from './src/components/ErrorBoundary';

// Token cache for native platforms (stores Clerk session securely)
let tokenCache;
if (Platform.OS !== 'web') {
  const SecureStore = require('expo-secure-store');
  tokenCache = {
    async getToken(key) {
      try {
        return SecureStore.getItemAsync(key);
      } catch (err) {
        return null;
      }
    },
    async saveToken(key, value) {
      try {
        return SecureStore.setItemAsync(key, value);
      } catch (err) {
        return;
      }
    },
  };
}

// Inner component that has access to theme context
function AppContent() {
  const { isDarkMode } = useTheme();
  const { appUser } = useAuth();
  const { showToast } = useToast();
  const seenFriendRequestIdsRef = useRef(new Set());

  useEffect(() => {
    // Listen for notifications when app is in foreground
    NotificationService.addNotificationReceivedListener((notification) => {
      console.log('📬 Notification received:', notification);
    });

    // Listen for notification taps
    NotificationService.addNotificationResponseReceivedListener((response) => {
      console.log('👆 Notification tapped:', response);
      
      const data = response.notification.request.content.data;
      
      // Navigate to chat if it's a message notification
      if (data.type === 'message') {
        console.log('Message notification tapped, should navigate to chat');
      }
    });

    return () => {
      NotificationService.removeListeners();
    };
  }, []);

  useEffect(() => {
    if (!appUser?.id) {
      seenFriendRequestIdsRef.current.clear();
      return;
    }

    let isActive = true;
    const channelName = `friend_requests_toast_${appUser.id}`;
    const resolveAvatarUrl = (rawValue) => {
      if (!rawValue) return null;
      return getProfilePictureUrl(rawValue, 'thumbnail') || rawValue;
    };

    const showIncomingFriendRequestToasts = async (specificRequestId = null) => {
      try {
        const pending = await getPendingFriendRequests(appUser.id);
        if (!isActive) return;

        const targetRequests = specificRequestId
          ? pending.filter((req) => req.id === specificRequestId)
          : pending;

        targetRequests.forEach((req) => {
          if (seenFriendRequestIdsRef.current.has(req.id)) return;
          const senderName = req?.sender?.username || req?.sender?.club_name || 'Someone';
          showToast(
            `${senderName} sent you a friend request`,
            'info',
            {
              avatarUrl: resolveAvatarUrl(
                req?.sender?.profile_picture_url || req?.sender?.profilePictureUrl
              ),
            }
          );
          seenFriendRequestIdsRef.current.add(req.id);
        });
      } catch (err) {
        console.error('Error checking incoming friend request toasts:', err);
      }
    };

    // Catch-up for requests that arrived while signed out/offline.
    showIncomingFriendRequestToasts();

    const subscription = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'friend_requests',
        },
        async (payload) => {
          const reqId = payload?.new?.id;
          const receiverId = payload?.new?.receiver_id;
          const senderId = payload?.new?.sender_id;
          if (!reqId || receiverId !== appUser.id || seenFriendRequestIdsRef.current.has(reqId)) return;

          try {
            const sender = senderId ? await getUserById(senderId) : null;
            if (!isActive) return;
            const senderName = sender?.username || sender?.club_name || 'Someone';
            showToast(
              `${senderName} sent you a friend request`,
              'info',
              {
                avatarUrl: resolveAvatarUrl(
                  sender?.profile_picture_url || sender?.profilePictureUrl
                ),
              }
            );
            seenFriendRequestIdsRef.current.add(reqId);
          } catch (err) {
            console.error('Error handling realtime friend request toast:', err);
            // Fallback to list check if sender fetch fails or lags.
            showIncomingFriendRequestToasts(reqId);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          showIncomingFriendRequestToasts();
        }
      });

    // Fallback for environments where realtime events are intermittently missed.
    const pollTimer = setInterval(() => {
      showIncomingFriendRequestToasts();
    }, 4000);

    return () => {
      isActive = false;
      clearInterval(pollTimer);
      subscription.unsubscribe();
    };
  }, [appUser?.id, showToast]);

  return (
    <>
      <AppNavigator />
      <StatusBar style={isDarkMode ? 'light' : 'dark'} />
    </>
  );
}

// Error boundary for Clerk configuration issues
function ClerkErrorBoundary({ children }) {
  if (!CLERK_PUBLISHABLE_KEY) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Clerk Publishable Key not configured</Text>
        <Text style={styles.errorSubtext}>
          Please set REACT_APP_CLERK_PUBLISHABLE_KEY or EXPO_PUBLIC_CLERK_PUBLISHABLE_KEY
        </Text>
      </View>
    );
  }
  return children;
}

export default function App() {
  // Keep native splash visible until we hide it (avoids default purple/loading screen)
  useEffect(() => {
    if (Platform.OS !== 'web') {
      SplashScreen.preventAutoHideAsync().catch(() => {});
    }
  }, []);

  // Remove the pre-React boot screen once we've mounted (web only)
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const boot = document.getElementById('expo-boot-screen');
      if (boot) boot.remove();
    }
  }, []);

  const clerkProviderProps = Platform.OS === 'web'
    ? { publishableKey: CLERK_PUBLISHABLE_KEY }
    : { publishableKey: CLERK_PUBLISHABLE_KEY, tokenCache };

  return (
    <View style={[styles.root, Platform.OS === 'web' && styles.rootWeb, isMobileWeb && styles.rootWebMobile]}>
      <ErrorBoundary>
        <ClerkErrorBoundary>
          <ClerkProvider {...clerkProviderProps}>
            <ClerkLoading>
              <View style={styles.loadingScreen} />
            </ClerkLoading>
            <ClerkLoaded>
              <ThemeProvider>
                <AuthProvider>
                  <MapProviderProvider>
                    <MapSettingsProvider>
                      <IconThemeProvider>
                        <ChatCacheProvider>
                          <ToastProvider>
                            <AppContent />
                          </ToastProvider>
                        </ChatCacheProvider>
                      </IconThemeProvider>
                    </MapSettingsProvider>
                  </MapProviderProvider>
                </AuthProvider>
              </ThemeProvider>
            </ClerkLoaded>
          </ClerkProvider>
        </ClerkErrorBoundary>
      </ErrorBoundary>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#1a1a2e',
  },
  rootWeb: {
    minHeight: '100vh',
  },
  rootWebMobile: {
    height: '100%',
    maxHeight: '100dvh',
    minHeight: '100dvh',
  },
  loadingScreen: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#1a1a2e',
  },
  errorText: {
    color: '#ff6b6b',
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  errorSubtext: {
    color: '#888',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 10,
  },
});
